import { dateFormatter } from '../../../utils/DateFormatter'
import { OptionalTimeZone } from '../../timezones'
import {
  ClasslessFilenameTemplateItem,
  ClasslessFilenameTemplateItemConstructor,
} from './ClasslessFilenameTemplateItem'

export abstract class FilenameTemplateItem<
  RecordType extends object,
  ClassType extends string,
  Property extends keyof RecordType & string,
  ID extends Lowercase<string>
> extends ClasslessFilenameTemplateItem<Property, ID> {
  /**
   * resolves the supplied record into its value
   *
   * ---
   *
   * this method uses a naive resolver and should be overwritten for any
   * RecordTypes that need special logic to resolve fields
   *
   * @param record
   * @param tz The IANA timezone identifier (e.g., "America/New_York") used for displaying dates
   * @see {@link resolve}
   * @see {@link resolveDate}
   */
  value<T extends RecordType>(
    record: T | null | undefined,
    _tz: OptionalTimeZone
  ): string {
    return this.resolve(record?.[this.property])
  }
  /**
   * a naive resolver which doesn't assume the datatype of {@link value}. it
   * will simply coerce the value to a string
   *
   * if the fallback option is enabled and value is null/undefined, the {@link example}
   * property will be used to resolve the value
   *
   * @param value the value a record pulled using {@link property}
   * @param options {@link ValueOptions}
   */
  protected resolve<T extends RecordType>(
    value: T[Property] | string | undefined | null
  ) {
    return value ? `${value}` : ''
  }
  /**
   * a Date resolver which wraps a defined value in {@link DateFormatter.file} before
   * passing it along to {@link resolve}
   *
   * @param value
   * @param options {@link ValueOptions}
   * @see {@link resolve}
   */
  protected resolveDate(value: Date | undefined | null, tz: OptionalTimeZone) {
    return value ? this.resolve(dateFormatter(tz).file(value)) : ''
  }
  /**
   * the class name associated with the {@link property}
   *
   * used to get the value for the filename
   */
  classType: ClassType
  /**
   * @see {@link ClasslessFilenameTemplateItem}
   */
  constructor({
    classType,
    ...options
  }: Pick<
    FilenameTemplateItem<RecordType, ClassType, Property, ID>,
    'classType'
  > &
    ClasslessFilenameTemplateItemConstructor<Property, ID>) {
    super(options)
    this.classType = classType
  }
}

export type ExtendedTemplateItemOptions<
  RecordType extends object,
  Property extends keyof RecordType & string,
  ID extends Lowercase<string>
> = ClasslessFilenameTemplateItemConstructor<Property, ID>
